import {CuratedSiteQuoteTerms} from "../../../Models/CuratedSiteQuoteTerms";
import {DownPaymentLinkRequest} from "../../../Models/DownPaymentLinkRequest";
import {CuratedSiteQuoteFailedMessage} from "../../../Models/CuratedSiteApplicationConfig";
// Type-only: the filters model imports from the shared library, which cannot load under the CRA Jest runner
import type {CuratedSiteApplicationFilters} from "../../../Models/CuratedSiteApplicationFilters";
import {getEffectiveMaxMonthlyPayment, getEffectiveMinMonthlyPayment} from "../CuratedVehiclesPage/monthlyPaymentCap";

// What the deposit link carries. The shopper's selections are in it so a refresh, the back button or a reopened link
// shows the same numbers; links made before they were added have none, and those send the shopper back to the sliders.
export interface DepositLinkParams {
  customerPortalLinkId: string;
  stockNumber: string;
  applicationId: string;
  depositAmount: number;
  downPayment?: number;
  monthlyPayment?: number;
}

// The application's slider limits (and fixed deposit), as loaded from the portal link
export type DepositLimits = Pick<CuratedSiteApplicationFilters,
  'minDownPayment' | 'maxDownPayment' | 'minMonthlyPayment' | 'maxMonthlyPayment' | 'paymentPercentOfMonthlyIncome' | 'fixedDepositAmount'>;

// Base64 of a JSON string array, the format the deposit route has always used and the same one Helpers/Utilities
// base64EncodeParams/base64DecodeParams produce. Written here with btoa/atob rather than those helpers because that
// module pulls in the shared library, which cannot load under the CRA Jest runner. Keep the two formats identical.
export const encodeDepositPageParameters = (params: Required<DepositLinkParams>): string =>
  btoa(JSON.stringify([
    params.customerPortalLinkId,
    params.stockNumber,
    params.applicationId,
    params.depositAmount.toString(),
    params.downPayment.toString(),
    params.monthlyPayment.toString()
  ]));

const toOptionalNumber = (value: unknown): number | undefined => {
  const parsed = typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN;
  return Number.isFinite(parsed) ? parsed : undefined;
};

// Null for anything that is not a deposit link (the link can be hand edited, so it must not throw)
export const decodeDepositPageParameters = (encoded: string | null | undefined): DepositLinkParams | null => {
  if (!encoded) {
    return null;
  }

  try {
    const values: unknown = JSON.parse(atob(encoded));
    if (!Array.isArray(values) || values.length < 4) {
      return null;
    }

    const [customerPortalLinkId, stockNumber, applicationId, depositAmount, downPayment, monthlyPayment] = values;
    return {
      customerPortalLinkId: String(customerPortalLinkId),
      stockNumber: String(stockNumber),
      applicationId: String(applicationId),
      depositAmount: toOptionalNumber(depositAmount) ?? 0,
      downPayment: toOptionalNumber(downPayment),
      monthlyPayment: toOptionalNumber(monthlyPayment)
    };
  } catch {
    return null;
  }
};

// The link is only base64, so a shopper can edit it. Its numbers must be ones the sliders could have produced: the down
// payment and monthly payment within the slider ranges (the monthly cap depends on the down payment), and the deposit the
// fixed deposit when there is one, otherwise the down payment. The server quotes from these and the processor charges the
// deposit, so anything else sends the shopper back to the sliders.
export const areDepositSelectionsWithinLimits = (params: DepositLinkParams, limits: DepositLimits): boolean => {
  const {downPayment, monthlyPayment, depositAmount} = params;
  if (downPayment === undefined || monthlyPayment === undefined || monthlyPayment <= 0) {
    return false;
  }

  const downPaymentInRange = downPayment >= Math.min(limits.minDownPayment, limits.maxDownPayment)
    && downPayment <= Math.max(limits.minDownPayment, limits.maxDownPayment);

  // The slider's whole-dollar bounds: a fractional minimum rounded up, and the cap floored but never below that minimum
  const effectiveMin = getEffectiveMinMonthlyPayment(limits.minMonthlyPayment);
  const effectiveMax = getEffectiveMaxMonthlyPayment(
    limits.minMonthlyPayment, limits.maxMonthlyPayment, limits.paymentPercentOfMonthlyIncome, downPayment);
  const monthlyPaymentInRange = monthlyPayment >= effectiveMin && monthlyPayment <= effectiveMax;

  const expectedDeposit = limits.fixedDepositAmount ?? downPayment;

  return downPaymentInRange && monthlyPaymentInRange && depositAmount === expectedDeposit;
};

// Only for a link whose selections passed areDepositSelectionsWithinLimits
export const buildDownPaymentLinkRequest = (
  clientId: string,
  params: DepositLinkParams
): DownPaymentLinkRequest => ({
  clientId: clientId,
  applicationId: params.applicationId,
  customerPortalLinkId: params.customerPortalLinkId,
  calculateQuoteAfterCallback: true,
  stockNumber: params.stockNumber,
  depositAmount: params.depositAmount,
  fullDownPaymentAmount: params.downPayment ?? 0,
  desiredMonthlyPayment: params.monthlyPayment ?? 0,
  spotVehicleAfterConfirmation: true
});

export interface FinancingTermsDisplay {
  regularPaymentsText: string; // e.g. "67 × $450.00"
  finalPaymentText?: string; // e.g. "+ 1 final payment of $345.32"; absent when every payment is the regular amount
  aprText: string;
}

const toPercent = (value: number): string => `${value.toFixed(2)}%`;

// Local formatter rather than the shared library's toDollars so this module stays free of that import
// (the library cannot be loaded under the CRA Jest runner).
const usd = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'});
const toCurrency = (value: number): string => usd.format(value);

// The confirmation screenshot records what the shopper accepted, so the terms are shown only when Carleton
// calculated a complete quote (term, payment and APR); otherwise nothing is shown rather than a partial or
// default-rate figure. Mirrors the LOS quoters: the regular-payment count, then "1 final payment" when Carleton
// returned an odd final payment. When the regular count is missing it is derived from the total so the final
// payment is not double counted. The server quotes monthly, so these are monthly payments.
export const getFinancingTermsDisplay = (quoteTerms: CuratedSiteQuoteTerms | null | undefined): FinancingTermsDisplay | null => {
  if (!quoteTerms?.quoteCalculated || !quoteTerms.numberOfPayments
    || quoteTerms.apr === null || quoteTerms.apr === undefined
    || quoteTerms.payment === null || quoteTerms.payment === undefined) {
    return null;
  }

  const hasFinalPayment = (quoteTerms.finalPayment ?? 0) > 0;
  // || rather than ??: a count of 0 is as unusable as a missing one
  const regularCount = quoteTerms.numberOfRegularPayments
    || (hasFinalPayment ? quoteTerms.numberOfPayments - 1 : quoteTerms.numberOfPayments);

  return {
    regularPaymentsText: `${regularCount} × ${toCurrency(quoteTerms.payment)}`,
    ...(hasFinalPayment && {finalPaymentText: `+ 1 final payment of ${toCurrency(quoteTerms.finalPayment as number)}`}),
    aprText: toPercent(quoteTerms.apr)
  };
};

export interface QuoteFailedScreenText {
  headerText: string;
  resultMessage: string;
  nextActionText: string;
  buttonText: string;
  buttonUrl: string | null; // null = back to the shopper's curated vehicle list
}

// Used only when the CuratedSiteQuoteFailedMessage item is missing or a field is blank; the real text lives in the item
const defaultQuoteFailedScreenText: QuoteFailedScreenText = {
  headerText: "WE'LL BE IN TOUCH!",
  resultMessage: 'A member of our team will reach out shortly to finish setting up your financing.',
  nextActionText: 'In the meantime, view our inventory or give us a call.',
  buttonText: 'VIEW INVENTORY',
  buttonUrl: null
};

// Non-breaking hyphens inside phone numbers (817-460-4646, (817) 460-4646) so a narrow screen never wraps one mid-number
const keepPhoneNumbersTogether = (text: string): string =>
  text.replace(/\b(\d{3})-(\d{3})-(\d{4})\b/g, '$1\u2011$2\u2011$3').replace(/\b(\d{3})-(\d{4})\b/g, '$1\u2011$2');

const textOrDefault = (value: string | null | undefined, fallback: string): string =>
  keepPhoneNumbersTogether(value && value.trim() ? value.trim() : fallback);

// The text for the screen shown instead of the deposit page when the quote fails completely
export const getQuoteFailedScreenText = (message: CuratedSiteQuoteFailedMessage | null | undefined): QuoteFailedScreenText => ({
  headerText: textOrDefault(message?.headerText, defaultQuoteFailedScreenText.headerText),
  resultMessage: textOrDefault(message?.resultMessage, defaultQuoteFailedScreenText.resultMessage),
  nextActionText: textOrDefault(message?.nextActionText, defaultQuoteFailedScreenText.nextActionText),
  buttonText: textOrDefault(message?.buttonText, defaultQuoteFailedScreenText.buttonText),
  buttonUrl: message?.buttonUrl && message.buttonUrl.trim() ? message.buttonUrl.trim() : null
});
